import { yupResolver } from '@hookform/resolvers/yup';
import { sendGTMEvent } from '@next/third-parties/google';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import EyeIcon from '@/assets/svg/eye-icon';
import { AuthLoadingOverlay, SsoProviders } from '@/components/modals/sso';
import { CTA_FONT } from '@/components/trial/lp/trial-beta/components/cta-font';
import { Alert, type AlertStateOrEmpty } from '@/components/ui/alert';
import CaptchaProvider from '@/components/ui/captcha-provider';
import type { CreateUserTrialParams } from '@/context/types';
import { useAuth } from '@/hooks/use-auth';
import { apiErrorMessageKey, TRIAL_STATUS_KEYS } from '@/utils/api-error-i18n';
import { personNameSchema } from '@/utils/person-name';
import { getUtmData } from '@/utils/user-tracking-data';

interface SignupFormProps {
  daysTrial: number;
  trialConfigId: number | null;
  trialPageId: number | null;
  isDisabledButton?: boolean;
  // Task 6 renders this form inline in the hero card; Task 12 reuses it inside a modal dialog.
  // The two variants share every field, schema and submit path — the only things this prop is
  // allowed to change are the outer container's width and, on the `<form>` itself, the
  // `np-trial-modal__form` class the modal variant needs (see below) — never a field, the schema
  // or the submit path.
  variant?: 'inline' | 'modal';
  // When given, the caller renders its own post-signup state (the beta welcome plus the
  // installer CTA) and this form stops navigating away. Without it the form keeps the
  // original `/thanks-for-trial-no-card` redirect, so the modal variant is unaffected.
  onSuccess?: () => void;
  // Lets a caller's stylesheet target the <form> directly (e.g. trial-beta.css's
  // `#v2b-hero-form` direct-child rules) — same pattern as CreateAccountV2's `formId` prop.
  formId?: string;
}

interface FormData {
  name: string;
  email: string;
  repeatEmail: string;
  password: string;
  passwordRepeat: string;
  recaptcha: string;
}

const INPUT_STYLE = { backgroundColor: '#2ECDFF08', borderColor: '#1F2730', color: '#E5E7EB', fontFamily: 'var(--font-outfit)' };
const INPUT_CLASS = 'w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-all focus:ring-2';

export function SignupForm({ daysTrial, trialConfigId, trialPageId, isDisabledButton = false, variant = 'inline', formId = 'signup-form', onSuccess }: SignupFormProps) {
  const { t } = useTranslation('components/trial/lp/trial-beta');
  const router = useRouter();
  const auth = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [popUp, setPopUp] = useState<{ show: boolean; message: string; state: AlertStateOrEmpty }>({ show: false, message: '', state: '' });
  const [isSsoAuthenticating, setIsSsoAuthenticating] = useState(false);

  const schema = yup.object().shape({
    name: personNameSchema(t('form.errors.empty_name'), t('form.errors.invalid_name')),
    email: yup.string().email(t('form.errors.invalid_email')).required(t('form.errors.empty_email')).max(255),
    repeatEmail: yup
      .string()
      .email(t('form.errors.invalid_email'))
      .required(t('form.errors.empty_email'))
      .max(255)
      .oneOf([yup.ref('email')], t('form.errors.repeat_email')),
    password: yup.string().required(t('form.errors.empty_password')).min(6, t('form.errors.short_password')),
    passwordRepeat: yup
      .string()
      .required(t('form.errors.empty_password'))
      .min(6, t('form.errors.short_password'))
      .oneOf([yup.ref('password')], t('form.errors.repeat_password')),
    recaptcha: yup.string().required(t('form.errors.empty_recaptcha')),
  });

  const {
    register,
    setError,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { name: '', email: '', repeatEmail: '', password: '', passwordRepeat: '', recaptcha: '' },
    mode: 'onBlur',
  });

  const handleRecaptchaChange = useCallback(
    (token: string | null) => {
      setValue('recaptcha', token ?? '');
      clearErrors('recaptcha');
    },
    [setValue, clearErrors]
  );

  const handleRecaptchaExpired = useCallback(() => {
    setValue('recaptcha', '');
    setError('recaptcha', { type: 'required', message: t('form.errors.empty_recaptcha') });
  }, [setValue, setError, t]);

  const handleRecaptchaError = useCallback(() => {
    setValue('recaptcha', '');
    if (typeof window !== 'undefined' && (window as any).captchaReset) {
      (window as any).captchaReset();
    }
  }, [setValue]);

  const handleSsoStart = useCallback(() => setIsSsoAuthenticating(true), []);

  const handleSsoSuccess = useCallback(() => setIsSsoAuthenticating(false), []);

  // An error ends the flow on this page.
  const handleSsoError = useCallback((message: string) => {
    setPopUp({ show: true, message, state: 'error' });
    setIsSsoAuthenticating(false);
  }, []);

  function handleLogin({ email, password, recaptcha }: { email: string; password: string; recaptcha: string }) {
    // eslint-disable-next-line func-names
    auth.login({ login: email, password, recaptcha }, function () {});
  }

  const onSubmit = async (data: FormData) => {
    if (isDisabledButton) return;
    setIsLoading(true);
    const utm = getUtmData();

    const body: CreateUserTrialParams = {
      name: data.name,
      email: data.email,
      password: data.password,
      recaptcha: data.recaptcha,
      telegram: null,
      password_repeat: data.passwordRepeat,
      ref: '',
      // `custom` IS the trial config id — the endpoint's name for it. Both ids have to reach the
      // API or the account is created with no trial at all, and it still answers 201.
      custom: trialConfigId,
      trialPageId,
      ...utm,
    };

    auth.createTrial(
      body,
      (error) => {
        setValue('recaptcha', '');
        if (typeof window !== 'undefined' && (window as any).captchaReset) (window as any).captchaReset();
        setPopUp({ show: true, message: t(apiErrorMessageKey(error, { fallbackKey: 'form.errors.default', statusKeys: TRIAL_STATUS_KEYS })), state: 'error' });
        setIsLoading(false);
      },
      () => {
        handleLogin({ email: data.email, password: data.password, recaptcha: data.recaptcha });
        sendGTMEvent({ event: 'createAccount' });
        sessionStorage.setItem('customTrialApproved', 'true');
        if (onSuccess) {
          onSuccess();
          setIsLoading(false);
          return;
        }
        router.push('/thanks-for-trial-no-card');
      },
      /* showSuccessToast */ false
    );
  };

  return (
    <div className={variant === 'modal' ? 'relative w-full' : 'relative mx-auto w-full max-w-xl'} data-variant={variant}>
      <AuthLoadingOverlay isActive={isSsoAuthenticating} />
      {popUp.show && <Alert message={popUp.message} state={popUp.state} onClose={() => setPopUp({ ...popUp, show: false })} />}

      {/* The source's clone adds this class straight onto the cloned `<form>`
          (`form.classList.add('np-trial-modal__form')`, trial-modal.js:7294) — it is the hook
          trial-beta.css's eight modal-only direct-child rules key off, so the modal variant needs
          it on the exact same element, at the exact same depth, or those rules never resolve. */}
      <form id={formId} onSubmit={handleSubmit(onSubmit)} className={classNames('flex w-full flex-col gap-2.5', variant === 'modal' && 'np-trial-modal__form')} autoComplete='off'>
        <div className='pb-2'>
          <SsoProviders onAuthStart={handleSsoStart} onAuthSuccess={handleSsoSuccess} onAuthError={handleSsoError} trialContext={{ custom: trialConfigId, trialPageId }} />
        </div>

        <div>
          <input data-lpignore='true' placeholder={t('form.name')} type='text' {...register('name')} dir='auto' autoComplete='name' className={classNames(INPUT_CLASS, errors.name && '!border-red-500')} style={INPUT_STYLE} maxLength={255} />
          {errors.name && <span className='mt-1 block text-xs text-red-500'>{errors.name.message}</span>}
        </div>

        <div>
          <input data-lpignore='true' placeholder={t('form.email')} type='email' {...register('email')} className={classNames(INPUT_CLASS, errors.email && '!border-red-500')} style={INPUT_STYLE} maxLength={255} />
          {errors.email && <span className='mt-1 block text-xs text-red-500'>{errors.email.message}</span>}
        </div>

        <div>
          <input data-lpignore='true' placeholder={t('form.repeat_email')} type='email' {...register('repeatEmail')} className={classNames(INPUT_CLASS, errors.repeatEmail && '!border-red-500')} style={INPUT_STYLE} maxLength={255} />
          {errors.repeatEmail && <span className='mt-1 block text-xs text-red-500'>{errors.repeatEmail.message}</span>}
        </div>

        <div>
          <div className='relative'>
            <input data-lpignore='true' placeholder={t('form.password')} type={showPassword ? 'text' : 'password'} {...register('password')} className={classNames(INPUT_CLASS, 'pe-10', errors.password && '!border-red-500')} style={INPUT_STYLE} />
            <button type='button' onClick={() => setShowPassword((previousValue) => !previousValue)} className='absolute end-3 top-1/2 -translate-y-1/2 text-[#515F6C] hover:opacity-80' aria-label='toggle password'>
              <EyeIcon />
            </button>
          </div>
          {errors.password && <span className='mt-1 block text-xs text-red-500'>{errors.password.message}</span>}
        </div>

        <div>
          <div className='relative'>
            <input data-lpignore='true' placeholder={t('form.repeat_password')} type={showPassword ? 'text' : 'password'} {...register('passwordRepeat')} className={classNames(INPUT_CLASS, 'pe-10', errors.passwordRepeat && '!border-red-500')} style={INPUT_STYLE} />
            <button type='button' onClick={() => setShowPassword((previousValue) => !previousValue)} className='absolute end-3 top-1/2 -translate-y-1/2 text-[#515F6C] hover:opacity-80' aria-label='toggle password'>
              <EyeIcon />
            </button>
          </div>
          {errors.passwordRepeat && <span className='mt-1 block text-xs text-red-500'>{errors.passwordRepeat.message}</span>}
        </div>

        <div className='flex items-center justify-center py-1'>
          <CaptchaProvider onChange={handleRecaptchaChange} onExpired={handleRecaptchaExpired} onError={handleRecaptchaError} />
        </div>
        {errors.recaptcha && <span className='block text-center text-xs text-red-500'>{errors.recaptcha.message}</span>}

        {/* The type spec is inline, and `text-sm font-bold` are gone rather than kept as a hint:
            the stylesheet's `.trial-beta-page button { font: inherit }` outranks them, so they
            styled nothing while suggesting they did (SCRUM-1886). */}
        <button type='submit' disabled={isDisabledButton || isLoading} className='w-full cursor-pointer rounded-lg border-none py-3.5 disabled:cursor-not-allowed disabled:opacity-60' style={{ background: '#93FF18', color: '#080809', ...CTA_FONT, boxShadow: '0 0 20px #93FF1840' }}>
          {isLoading ? t('form.submitting') : t('form.submit', { count: daysTrial })}
        </button>
      </form>
    </div>
  );
}
